/**
 * IconSelector Component
 *
 * Icon picker for Feature Section items
 * Supports both icon library selection and custom SVG paste
 *
 * Features:
 * - Tab interface for library vs custom SVG
 * - Icon library browser with common icons
 * - Custom SVG paste field with validation
 * - Preview of selected icon
 * - Remove functionality
 *
 * Props:
 * - value: Icon object { type: "library"|"custom", value: string }
 * - onChange: Callback when icon changes
 * - label: Field label
 * - isEditMode: Boolean for edit/view mode
 */

import React, { useState } from "react";

// Common icon library (subset of icons from IconComponent)
const ICON_LIBRARY = [
  { name: "check", label: "Check" },
  { name: "check-solid", label: "Check (Solid)" },
  { name: "success", label: "Success" },
  { name: "warning", label: "Warning" },
  { name: "info", label: "Info" },
  { name: "error", label: "Error" },
  { name: "home", label: "Home" },
  { name: "building", label: "Building" },
  { name: "users", label: "Users" },
  { name: "calendar", label: "Calendar" },
  { name: "chart", label: "Chart" },
  { name: "settings", label: "Settings" },
  { name: "plus", label: "Plus" },
  { name: "edit", label: "Edit" },
  { name: "trash", label: "Trash" },
  { name: "save", label: "Save" },
  { name: "download", label: "Download" },
  { name: "search", label: "Search" },
  { name: "filter", label: "Filter" },
  { name: "envelope", label: "Email" },
  { name: "bell", label: "Notification" },
  { name: "chat", label: "Chat" },
];

export default function IconSelector({
  value = null,
  onChange,
  label = "Icon",
  isEditMode = true,
}) {
  const [activeTab, setActiveTab] = useState("library");
  const [customSvg, setCustomSvg] = useState(
    value?.type === "custom" ? value.value : "",
  );

  if (!isEditMode) {
    // View mode - just show the icon if present
    if (!value || !value.value) return null;

    if (value.type === "custom") {
      return (
        <div
          className="w-6 h-6 text-blue-600"
          dangerouslySetInnerHTML={{ __html: value.value }}
        />
      );
    } else {
      // Render library icon using CSS classes that match the IconComponent
      return <div className="w-6 h-6">📌</div>; // Placeholder - actual rendering handled by ViewComponent
    }
  }

  const handleLibrarySelect = (iconName) => {
    onChange({
      type: "library",
      value: iconName,
    });
  };

  const handleCustomSvgPaste = (e) => {
    const svg = e.target.value.trim();
    setCustomSvg(svg);

    // Simple validation - must start with <svg and end with </svg>
    if (svg.startsWith("<svg") && svg.endsWith("</svg>")) {
      onChange({
        type: "custom",
        value: svg,
      });
    }
  };

  const handleRemove = () => {
    onChange(null);
    setCustomSvg("");
  };

  return (
    <div className="icon-selector mb-4">
      <label className="block text-sm font-medium text-neutral-700 mb-2">
        {label}
      </label>

      {value && value.value ? (
        <div className="flex items-center gap-3 p-3 border rounded bg-white">
          {/* Icon preview */}
          <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded">
            {value.type === "custom" ? (
              <div
                className="w-6 h-6 text-blue-600"
                dangerouslySetInnerHTML={{ __html: value.value }}
              />
            ) : (
              <span className="text-blue-600">📌</span>
            )}
          </div>

          {/* Icon info */}
          <div className="flex-1">
            <div className="text-sm font-medium">
              {value.type === "library" ? value.value : "Custom SVG"}
            </div>
            <div className="text-xs text-neutral-500">
              {value.type === "library" ? "From library" : "Custom code"}
            </div>
          </div>

          {/* Remove button */}
          <button
            type="button"
            onClick={handleRemove}
            className="text-red-600 hover:text-red-800 text-sm px-2 py-1"
          >
            Remove
          </button>
        </div>
      ) : (
        <div className="border rounded p-4">
          {/* Tabs */}
          <div className="flex gap-2 mb-4 border-b">
            <button
              type="button"
              onClick={() => setActiveTab("library")}
              className={`px-3 py-2 text-sm font-medium ${
                activeTab === "library"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-neutral-500 hover:text-neutral-700"
              }`}
            >
              Icon Library
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("custom")}
              className={`px-3 py-2 text-sm font-medium ${
                activeTab === "custom"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-neutral-500 hover:text-neutral-700"
              }`}
            >
              Custom SVG
            </button>
          </div>

          {/* Tab content */}
          {activeTab === "library" && (
            <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto">
              {ICON_LIBRARY.map((icon) => (
                <button
                  key={icon.name}
                  type="button"
                  onClick={() => handleLibrarySelect(icon.name)}
                  className="p-3 border rounded hover:border-blue-500 hover:bg-blue-50 transition-colors flex flex-col items-center gap-1"
                  title={icon.label}
                >
                  <span className="text-2xl">📌</span>
                  <span className="text-xs text-neutral-600 truncate w-full text-center">
                    {icon.label}
                  </span>
                </button>
              ))}
            </div>
          )}

          {activeTab === "custom" && (
            <div>
              <textarea
                value={customSvg}
                onChange={(e) => setCustomSvg(e.target.value)}
                onBlur={handleCustomSvgPaste}
                placeholder="Paste SVG code here (e.g., <svg>...</svg>)"
                className="form-control font-mono text-sm"
                rows={6}
                maxLength={2000}
              />
              <p className="text-xs text-neutral-500 mt-2">
                Paste a complete SVG code block. It will be sanitized for
                security. Max 2000 characters.
              </p>
              {customSvg && !customSvg.startsWith("<svg") && (
                <p className="text-xs text-red-600 mt-1">
                  Invalid SVG: Must start with &lt;svg&gt;
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
